import {
  useCachedBusinessAddress,
  useCachedBusinessInsights,
  useCachedBusinessName,
  useCachedConversationalWritingRule,
  useCachedGeographicFocus,
  useCachedSelectedProject,
  useCachedStatisticalEvidenceRule,
  useCachedTargetAudience,
} from 'hooks';
import { useCachedContentFreshnessRule } from 'hooks/api/cached/useCachedContentFreshnessRule';
import { useTechnicalAccessibilityState } from './useTechnicalAccessibilityState';
import { RuleStat } from 'shared/helpers/technicalOverviewStats';

const createRuleStat = (total: number, pass: number): RuleStat => ({
  total,
  pass,
  needFix: Math.max(total - pass, 0),
});

export const useRuleStats = () => {
  const { isTrialExpired } = useCachedSelectedProject();
  const { services } = useCachedBusinessInsights();
  const { passed: converPassed, isLoaded: converLoaded } =
    useCachedConversationalWritingRule();
  const { passed: freshnessPassed, isLoaded: freshnessLoaded } =
    useCachedContentFreshnessRule();
  const { passed: statisticalPassed, isLoaded: statisticalLoaded } =
    useCachedStatisticalEvidenceRule();
  const {
    passed: businessNamePassed,
    isLoaded: businessNameLoaded,
    rulesCount: bnRulesTotal,
  } = useCachedBusinessName();
  const {
    passed: businessAddressPassed,
    isLoaded: businessAddressLoaded,
    rulesCount: baRulesTotal,
  } = useCachedBusinessAddress();
  const {
    passed: targetAudiencePassed,
    isLoaded: targetAudienceLoaded,
    rulesCount: taRulesTotal,
  } = useCachedTargetAudience();
  const {
    passed: geographicFocusPassed,
    isLoaded: geographicFocusLoaded,
    rulesCount: gfRulesTotal,
  } = useCachedGeographicFocus();
  const {
    technicalStat,
    technicalLoading,
    hasTechRules,
    showTechnicalSection,
    allowDashboard,
  } = useTechnicalAccessibilityState();
  const productsTotal = (services?.length ?? 0) * 3;
  const productsPass = converPassed + freshnessPassed + statisticalPassed;
  const productsStat = createRuleStat(productsTotal, productsPass);

  const completenessTotal =
    taRulesTotal + gfRulesTotal + bnRulesTotal + baRulesTotal;
  const completenessPass =
    businessNamePassed +
    businessAddressPassed +
    targetAudiencePassed +
    geographicFocusPassed;
  const completenessStat = createRuleStat(completenessTotal, completenessPass);
  const improvementNeedFix =
    productsStat.needFix +
    completenessStat.needFix +
    (hasTechRules ? technicalStat.needFix : 0);

  const completenessLoading = isTrialExpired
    ? false
    : !geographicFocusLoaded ||
      !targetAudienceLoaded ||
      !businessAddressLoaded ||
      !businessNameLoaded;
  const productsLoading = isTrialExpired
    ? false
    : !statisticalLoaded || !freshnessLoaded || !converLoaded;
  const overviewLoading =
    completenessLoading || productsLoading || technicalLoading;

  return {
    productsStat,
    completenessStat,
    technicalStat,
    improvementNeedFix,
    completenessLoading,
    productsLoading,
    technicalLoading,
    overviewLoading,
    hasTechRules,
    showTechnicalSection,
    allowDashboard,
  };
};
